import { useEffect, type ReactNode } from "react";
import { createPortal } from "react-dom";

const OVERLAY_CLASS =
  "gs-modal-overlay fixed inset-0 z-[100] flex items-center justify-center overscroll-none bg-[#F4F6F8]/95 p-4";

type ModalBackdropProps = {
  open: boolean;
  children: ReactNode;
  /** Se definido, clique no fundo e Escape disparam este callback. */
  onBackdropClick?: () => void;
  className?: string;
  id?: string;
};

/**
 * Overlay de modal na raiz do documento (evita perder o backdrop quando o modal
 * está dentro de abas/layout com transform ou stacking context).
 */
export function ModalBackdrop({ open, children, onBackdropClick, className = "", id }: ModalBackdropProps) {
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  useEffect(() => {
    if (!open || !onBackdropClick) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onBackdropClick();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onBackdropClick]);

  if (!open || typeof document === "undefined") return null;

  return createPortal(
    <div
      id={id}
      className={`${OVERLAY_CLASS} ${className}`.trim()}
      role="dialog"
      aria-modal="true"
      onClick={onBackdropClick}
    >
      {children}
    </div>,
    document.body,
  );
}
